import React, {useState, useRef, useEffect, useCallback} from 'react';
import {View, Text, TouchableOpacity, StyleSheet, Platform} from 'react-native';
import {createPortal} from 'react-dom';
import {useSelector} from 'react-redux';
import useToteStatus from './useToteStatus';
import { toteContainsOrder, getToteOrder } from './utils/scanningUtils';
import { calculateOptimalCirclesPerRow, calculateCircleSize } from './utils/scanProgressUtils';

const revealBubbleStyle = {
  position: 'fixed',
  zIndex: 99999,
  backgroundColor: '#000',
  color: '#e8e8e8',
  fontFamily: 'Poppins_600SemiBold, Poppins, sans-serif',
  fontWeight: 600,
  fontSize: 16,
  padding: '5px 10px',
  borderRadius: 4,
  maxWidth: 320,
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxShadow: '0 2px 8px rgba(0,0,0,0.65)',
};

export const TOTE_REVEAL_TIMEOUT_MS = 4000;
export const TOTE_LONG_PRESS_DELAY_MS = 500;
export const TOUCH_COMPAT_WINDOW_MS = 1000;
export const REVEAL_EDGE_MARGIN_PX = 8;
export const REVEAL_TOTE_GAP_PX = 2;

const revealBubbleTransform = x =>
  `translateX(clamp(${REVEAL_EDGE_MARGIN_PX - x}px, -50%, calc(100vw - ${REVEAL_EDGE_MARGIN_PX + x}px - 100%))) translateY(-100%)`;

const ToteStatusGrid = ({onTotePress = () => {}, onToteFocus = () => {}}) => {
  const cartOrders = useSelector(state => state.scanToCart.cartOrders);
  const cartScanComplete = useSelector(state => state.scanToCartScanning.cartScanComplete);
  const focusedOrderId = useSelector(state => state.scanToCartScanning.focusedOrderId);
  const [reveal, setReveal] = useState(null);
  const revealTimer = useRef(null);
  const touchActive = useRef(false);
  const lastTouchAt = useRef(0);
  const swallowTouchEndClick = useRef(false);
  const {
    activeCart,
    nextPickOrderId,
    activeTote,
    getToteItemPieces,
    getToteStatus,
    calculateToteHeight,
  } = useToteStatus();
  const fullyPicked = Boolean(cartScanComplete || activeCart?.isPicked);

  const clearRevealTimer = () => {
    if (revealTimer.current) {
      clearTimeout(revealTimer.current);
      revealTimer.current = null;
    }
  };

  const clearReveal = useCallback(() => {
    if (revealTimer.current) {
      clearTimeout(revealTimer.current);
      revealTimer.current = null;
    }
    setReveal(null);
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(revealTimer.current);
      revealTimer.current = null;
    };
  }, []);

  useEffect(() => {
    if (reveal?.source !== 'touch' || typeof document === 'undefined') return undefined;
    document.addEventListener('touchstart', clearReveal, true);
    return () => document.removeEventListener('touchstart', clearReveal, true);
  }, [reveal, clearReveal]);

  useEffect(() => {
    if (fullyPicked) clearReveal();
  }, [fullyPicked, clearReveal]);

  const hasRecentTouch = () =>
    touchActive.current || Date.now() - lastTouchAt.current < TOUCH_COMPAT_WINDOW_MS;

  const handleTouchStart = () => {
    touchActive.current = true;
    swallowTouchEndClick.current = false;
    lastTouchAt.current = Date.now();
  };

  const handleTouchEnd = event => {
    touchActive.current = false;
    lastTouchAt.current = Date.now();
    if (swallowTouchEndClick.current) {
      swallowTouchEndClick.current = false;
      event?.preventDefault?.();
    }
  };

  // Safari 18.2+ on iOS reports pointerType "mouse" on a tap's click, so own touch tracking wins: https://bugs.webkit.org/show_bug.cgi?id=282988
  const getPressSource = event => {
    if (hasRecentTouch()) return 'touch';
    return event?.nativeEvent?.pointerType === 'touch' ? 'touch' : 'mouse';
  };

  const showReveal = (tote, order, source, event) => {
    clearRevealTimer();
    const rect = event?.currentTarget?.getBoundingClientRect?.();
    const nativeEvent = event?.nativeEvent;
    setReveal({
      toteKey: tote.id,
      text: `#${order.orderNumber}`,
      source,
      x: rect ? rect.left + rect.width / 2 : nativeEvent?.clientX ?? 0,
      y: rect ? rect.top : nativeEvent?.clientY ?? 0,
    });
    if (source === 'touch') {
      revealTimer.current = setTimeout(() => {
        revealTimer.current = null;
        setReveal(null);
      }, TOTE_REVEAL_TIMEOUT_MS);
    }
  };

  const hideReveal = (tote, source) => {
    setReveal(current => {
      if (current && current.toteKey === tote.id && current.source === source) return null;
      return current;
    });
  };

  const focusOrder = order => {
    if (order.orderId !== focusedOrderId) onToteFocus(order.orderId);
  };

  const handleMouseEnter = (tote, order, event) => {
    if (!order || hasRecentTouch()) return;
    showReveal(tote, order, 'hover', event);
  };

  const handleMouseLeave = tote => {
    if (hasRecentTouch()) return;
    hideReveal(tote, 'hover');
  };

  const handleToteFocus = (tote, order, event) => {
    if (!order || hasRecentTouch()) return;
    const target = event?.currentTarget;
    if (target?.matches && !target.matches(':focus-visible')) return;
    showReveal(tote, order, 'focus', event);
  };

  const handleToteBlur = tote => {
    hideReveal(tote, 'focus');
  };

  const handlePress = (tote, order, event) => {
    if (fullyPicked) {
      onTotePress(tote);
      return;
    }
    if (!order) return;
    if (getPressSource(event) === 'touch') {
      showReveal(tote, order, 'touch', event);
      return;
    }
    focusOrder(order);
  };

  // GROOV-5346: entering focus shifts the layout, so the browser's compat click on touchend would land on whatever moved under the finger
  const handleLongPress = (tote, order) => {
    if (!order) return;
    swallowTouchEndClick.current = true;
    focusOrder(order);
  };

  const handleContextMenu = event => {
    if (hasRecentTouch()) event?.preventDefault?.();
  };

  const toteSpacing = 6;
  const rowSpacing = 9;

  const renderCircle = (piece, index, circleConfig, status) => {
    const dynamicCircleBase = {
      width: circleConfig.size,
      height: circleConfig.size,
      borderRadius: circleConfig.borderRadius,
      borderWidth: circleConfig.borderWidth,
      marginHorizontal: 2,
    };

    let circleStyle = [dynamicCircleBase];

    switch (piece.status) {
      case 'required':
        circleStyle = [dynamicCircleBase, styles.circleRequired];
        break;
      case 'picked':
        circleStyle = [dynamicCircleBase, styles.circlePicked, {opacity: status === 'current' ? 1 : 0.6}];
        break;
      case 'picked_and_placed':
        circleStyle = [dynamicCircleBase, styles.circlePickedAndPlaced];
        break;
      default:
        circleStyle = [dynamicCircleBase, styles.circleDefault];
        break;
    }

    return <View key={index} style={circleStyle} />;
  };

  const renderCircleGrid = (itemPieces, status) => {
    if (itemPieces.length === 0) return null;

    const totalCircles = itemPieces.length;
    const circlesPerRow = calculateOptimalCirclesPerRow(totalCircles);
    const circleConfig = calculateCircleSize(totalCircles);
    const rows = [];

    for (let i = 0; i < itemPieces.length; i += circlesPerRow) {
      const rowPieces = itemPieces.slice(i, i + circlesPerRow);
      rows.push(
        <View key={i} style={styles.circleRow}>
          {rowPieces.map((piece, index) => renderCircle(piece, i + index, circleConfig, status))}
        </View>
      );
    }

    return (
      <View style={styles.circleGrid}>
        {rows}
      </View>
    );
  };

  const getToteStyles = (status, toteHeight, hasLowOpacity = false,isNextPick = false, activeTote = false, isFocused = false) => {
    const baseStyle = [styles.toteBase, {height: toteHeight}];

    let styleArray;
    switch (status) {
      case 'unused':
        styleArray = [baseStyle, styles.toteUnused];
        break;
      case 'empty':
        styleArray = [baseStyle, styles.toteEmpty];
        break;
      case 'partial':
        styleArray = [baseStyle, styles.totePartial];
        break;
      case 'complete':
        styleArray = [baseStyle, styles.toteComplete];
        break;
      case 'current':
        styleArray = [baseStyle, styles.toteCurrent];
        break;
      default:
        styleArray = baseStyle;
    }

    if (hasLowOpacity && !isNextPick) {
      styleArray = [...styleArray, {opacity: 0.6}];
    }
    if (isNextPick || activeTote) {
      styleArray = [...styleArray, styles.toteNextPick];
    }
    if (isFocused) {
      styleArray = [...styleArray, styles.toteFocused];
    }
    return styleArray;
  };

  const getToteTextStyle = status => {
    return status === 'unused' ? styles.toteTextUnused : styles.toteText;
  };

  const renderTote = (tote, rowHeight) => {
    const status = getToteStatus(tote);
    const itemPieces = getToteItemPieces(tote.tote_id);
    const hasLowOpacity = (tote.assign_order_to_totes || []).every(item => item.isPrinted);
    const isNextPick = toteContainsOrder(tote, nextPickOrderId);
    const isActiveTote = activeTote?.cart_tote_id === tote?.id
    const order = getToteOrder(tote, cartOrders);
    const isFocused = focusedOrderId !== null && focusedOrderId !== undefined
      && toteContainsOrder(tote, focusedOrderId);
    const toteStyles = getToteStyles(status, rowHeight, hasLowOpacity,isNextPick, isActiveTote, isFocused);
    const accessibilityLabel = order
      ? `Tote ${tote.tote_id}, order #${order.orderNumber}`
      : `Tote ${tote.tote_id}`;

    return (
      <TouchableOpacity
        key={tote.id}
        style={styles.toteTouchable}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        delayLongPress={TOTE_LONG_PRESS_DELAY_MS}
        onPress={event => handlePress(tote, order, event)}
        onLongPress={fullyPicked ? undefined : () => handleLongPress(tote, order)}
        onMouseEnter={event => handleMouseEnter(tote, order, event)}
        onMouseLeave={() => handleMouseLeave(tote)}
        onFocus={event => handleToteFocus(tote, order, event)}
        onBlur={() => handleToteBlur(tote)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onContextMenu={handleContextMenu}
        activeOpacity={0.7}>
        <View style={toteStyles} testID={`tote-${tote.tote_id}`}>
          <View style={styles.toteContent}>
            <Text style={getToteTextStyle(status)}>{tote.tote_id}</Text>
            {renderCircleGrid(itemPieces, status)}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderRow = row => {
    const maxRowHeight = Math.max(
      ...row.cart_totes.map(tote => {
        const itemPieces = getToteItemPieces(tote.tote_id);
        return calculateToteHeight(itemPieces);
      })
    );

    return (
      <View key={row.id} style={[styles.row, {marginBottom: rowSpacing, paddingHorizontal: 8}]}>
        <View style={[styles.rowContent, {gap: toteSpacing}]}>
          {row.cart_totes.map(tote => (
            <View key={tote.id} style={styles.toteWrapper}>
              {renderTote(tote, maxRowHeight)}
            </View>
          ))}
        </View>
      </View>
    );
  };

  if (!activeCart || !activeCart.cart_rows) {
    return (
      <View style={[styles.container, styles.emptyContainer]}>
        <Text style={styles.emptyText}>No cart data available</Text>
      </View>
    );
  }

  return (
    <View style={styles.container} testID="toteStatusGrid">
      {activeCart.cart_rows.map(row => renderRow(row))}
      {reveal && Platform.OS === 'web' && typeof document !== 'undefined' && createPortal(
        <div
          role="tooltip"
          style={{...revealBubbleStyle, left: reveal.x, top: reveal.y - REVEAL_TOTE_GAP_PX, transform: revealBubbleTransform(reveal.x)}}>
          {reveal.text}
        </div>,
        document.body
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'transparent',
    width: '100%',
  },
  emptyContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  emptyText: {
    color: '#fff',
    fontSize: 16,
  },
  row: {
    // Dynamic marginBottom and paddingHorizontal applied inline
  },
  rowContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    // Dynamic gap applied inline
  },
  toteWrapper: {
    flex: 1,
  },
  toteBase: {
    width: '100%',
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    backgroundColor: 'transparent',
  },
  toteUnused: {
    borderColor: '#666',
    borderStyle: 'dashed',
  },
  toteEmpty: {
    borderColor: '#888',
  },
  totePartial: {
    borderColor: '#888',
  },
  toteComplete: {
    borderColor: '#4CAF50',
  },
  toteCurrent: {
    borderColor: '#FF8C00',
    borderWidth: 3,
  },
  toteContent: {
    alignItems: 'center',
  },
  toteText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#fff',
  },
  toteTextUnused: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'rgba(255, 255, 255, 0.4)',
  },
  circleGrid: {
    alignItems: 'center',
    marginTop: 3,
  },
  circleRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginVertical: 1.5,
  },
  circleRequired: {
    backgroundColor: 'transparent',
    borderColor: '#fff',
  },
  circlePicked: {
    backgroundColor: 'transparent',
    borderColor: '#FFD700',
  },
  circlePickedAndPlaced: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  circleDefault: {
    backgroundColor: 'transparent',
    borderColor: '#666',
  },
  toteNextPick: {
    borderColor: '#fff',
    borderWidth: 3,
  },
  toteFocused: {
    borderColor: '#00BFFF',
    borderWidth: 5,
    opacity: 1,
  },
  toteTouchable: {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
  },
});

export default ToteStatusGrid;
